import { FileText, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LoadError } from '../../components/layout/LoadError';
import { useLetters, useMyCapabilities, type LetterStatus, type LetterType } from '../../hooks';
import { useAuthStore } from '../../lib/store';

const TYPE_LABEL: Record<LetterType, string> = {
  employment_cert: 'หนังสือรับรองการทำงาน',
  salary_cert: 'หนังสือรับรองเงินเดือน',
  warning: 'หนังสือเตือน',
};

const STATUS: Record<LetterStatus, { label: string; className: string }> = {
  draft: { label: 'ร่าง', className: 'bg-slate-100 text-slate-700' },
  issued: { label: 'ออกแล้ว', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'ยกเลิก', className: 'bg-gray-100 text-gray-500' },
};

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('th-TH', {
        timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric',
      });
}

export function EmployeeLettersSection({ empCode }: { empCode: string }) {
  const navigate = useNavigate();
  const letters = useLetters({ emp: empCode });
  // ปุ่มออกหนังสือเตือนโชว์เฉพาะคนที่ worker ยอม (OWNER หรือ HR ที่มี edit_employee) — ไม่งั้นกดแล้ว 403
  const role = useAuthStore((s) => s.profile?.role);
  const { data: caps = [] } = useMyCapabilities();
  const canWarn = role === 'OWNER' || caps.includes('edit_employee');
  const rows = [...(letters.data?.items ?? [])].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-500">
          <FileText className="h-3.5 w-3.5" /> หนังสือ HR / ประวัติการเตือน
        </div>
        <div className="flex flex-wrap gap-2">
          {canWarn && (
            <button
              type="button"
              onClick={() => navigate(`/hr/letters/new?type=warning&emp=${encodeURIComponent(empCode)}`)}
              className="btn-primary min-h-11 flex-1 text-sm sm:flex-none"
            >
              <Plus className="h-4 w-4" /> ออกหนังสือเตือน
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/hr/letters?emp=${encodeURIComponent(empCode)}`)}
            className="btn-secondary min-h-11 flex-1 text-sm sm:flex-none"
          >
            ดูทั้งหมด
          </button>
        </div>
      </div>

      {letters.isLoading ? (
        <p className="py-3 text-center text-sm text-gray-500">กำลังโหลด…</p>
      ) : letters.error ? (
        <LoadError error={letters.error} onRetry={() => void letters.refetch()} />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-500">
          ยังไม่มีหนังสือของพนักงานคนนี้
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {rows.map((row) => {
            const status = STATUS[row.status];
            const isWarning = row.letter_type === 'warning';
            return (
              <li key={row.id} className="flex items-start gap-2.5 px-3 py-3">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${isWarning ? 'bg-amber-500' : 'bg-gray-300'}`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-gray-800">{TYPE_LABEL[row.letter_type]}</span>
                    <span className={`rounded px-2 py-0.5 text-xs ${status.className}`}>{status.label}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    เลขที่ {row.doc_no ?? 'ร่าง'} · วันที่ออก {thaiDate(row.issued_at)}
                  </div>
                  {row.ack && (
                    <div className="mt-1 text-xs text-green-700">
                      {row.ack.method === 'screen' ? 'เซ็นรับทราบบนจอ' : 'ลงนามกระดาษ'} {thaiDate(row.ack.method === 'paper' ? row.ack.signed_on : row.ack.acked_at)}
                    </div>
                  )}
                  {row.needs_ack && <div className="mt-1 text-xs text-amber-700">รอพนักงานลงนามรับทราบ</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
